import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Row, Col, ListGroup, Card, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Message from '../components/Message';
import Loader from '../components/Loader';
import { useCreateOrderMutation } from '../slices/ordersApiSlice';
import { clearCartItems } from '../slices/cartSlice';

const PlaceOrderScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const cart = useSelector((state) => state.cart);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [shippingLabel, setShippingLabel] = useState(null);

  const [createOrder, { isLoading, error }] = useCreateOrderMutation();

  const totalPrice = cart.cartItems
    .reduce((acc, item) => acc + Number(item.offer_price || 0), 0)
    .toFixed(2);

  const totalBooks = cart.cartItems.length;

  const isStaleItem = (item) => {
    const timestamp = item.lastRefreshedAt || item.updatedAt || item.addedAt || item.createdAt;
    if (!timestamp) return true;
    return Date.now() - Number(timestamp) > 48 * 60 * 60 * 1000;
  };

  const isValidPrice = (item) => Number(item.offer_price) > 0;
  const hasPendingItems = cart.cartItems.some((item) => !isValidPrice(item) || isStaleItem(item));



  const placeOrderHandler = async () => {
    if (hasPendingItems) {
      toast.error('Vernieuw of verwijder de items uit de rode en oranje lijst voordat je verder gaat.');
      return;
    }

    if (!privacyAccepted) {
      toast.error('Je moet onze privacyverklaring accepteren voordat je een bestelling kunt versturen.');
      return;
    }

    try {
      const orderItems = cart.cartItems.map(item => ({
        title: item.title,
        author: item.author,
        isbn: item.isbn,
        offer_price: Number(item.offer_price),
      }));

      const response = await createOrder({
        orderItems,
        itemsPrice: Number(totalPrice),
      }).unwrap();

      if (response) {
        setShippingLabel(response.shippingLabel);
        dispatch(clearCartItems());
        toast.success('Bestelling geplaatst! Verzendlabel is aangemaakt.');
        
        // Redirect to shipping label screen after 1 second
        setTimeout(() => {
          navigate(`/order/${response._id}/label`);
        }, 1000);
      }
    } catch (err) {
      console.log(err);
      toast.error(err?.data?.message || err.error || 'Fout bij het plaatsen van bestelling');
    }
  };

  return (
    <Row>
      <Col md={8}>
        <ListGroup variant="flush">
          <ListGroup.Item>
            <h2>Uw Boekenlijst</h2>
            <Message variant='info'>
              Aangeboden prijs geldt alleen voor de komende 48 uur. Na 48 uur moet elk boek opnieuw worden vernieuwd voordat het kan worden verzonden.
            </Message>
            {hasPendingItems && (
              <Message variant='warning'>
                Er staan nog boeken in de rode of oranje lijst. Vernieuw of verwijder deze items voordat je verder gaat.
              </Message>
            )}
            {cart.cartItems.length === 0 ? (
              <Message>Uw winkelwagen is leeg <Link to='/'>Ga terug</Link></Message>
            ) : (
              <ListGroup variant="flush">
                {cart.cartItems.map((item, index) => (
                  <ListGroup.Item key={index}>
                    <Row className="align-items-center">
                      <Col md={1}>{index + 1}</Col>
                      <Col>
                        <div style={{ fontSize: "1.2rem" }}>{item.title}</div>
                        {item.author && (
                          <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                            <strong>{item.isbn}</strong> {item.author}
                          </div>
                        )}
                      </Col>
                      <Col md={3} className="text-end">
                        € {item.offer_price}
                      </Col>
                    </Row>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </ListGroup.Item>

          <ListGroup.Item>
            <Message variant='info'>
              ✅ Boeken worden verzonden naar: <strong>Meint Veningastraat 22, 9601 KG Hoogezand</strong>
            </Message>
          </ListGroup.Item>
        </ListGroup>
      </Col>

      <Col md={4}>
        <Card>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <h2>Uw Bestelling</h2>
            </ListGroup.Item>
            <ListGroup.Item>
              <Row>
                <Col>Totaal Bedrag</Col>
                <Col>€ {totalPrice}</Col>
              </Row>
            </ListGroup.Item>
            <ListGroup.Item>
              <Row>
                <Col>Aantal Boeken</Col>
                <Col>{totalBooks}</Col>
              </Row>
            </ListGroup.Item>

            {shippingLabel && (
              <ListGroup.Item>
                <Message variant="success">
                  ✅ Verzendlabel aangemaakt!
                  <br />
                  Trackingnummer: <strong>{shippingLabel.trackingNumber}</strong>
                  <br />
                  Gewicht: <strong>{Number(shippingLabel.weightKg || 0).toFixed(2)} kg</strong>
                  <br />
                  <small>Label is per e-mail naar u verstuurd</small>
                </Message>
              </ListGroup.Item>
            )}

            {error && (
              <ListGroup.Item>
                <Message variant="danger">{error.data?.message}</Message>
              </ListGroup.Item>
            )}

            <ListGroup.Item>
              <Form.Check
                type='checkbox'
                id='privacy-acceptance'
                label={
                  <span>
                    Ik ga akkoord met de privacyverklaring en begrijp dat de boeken in goede staat moeten worden verzonden.
                    <Link to='/privacy' target='_blank' rel='noopener noreferrer' className='ms-1'>Bekijk privacy</Link>
                  </span>
                }
                checked={privacyAccepted}
                onChange={(e) => setPrivacyAccepted(e.target.checked)}
                className='mb-3'
              />
              <Button
                type="button"
                className="btn-block w-100"
                disabled={cart.cartItems.length === 0 || hasPendingItems || !privacyAccepted || isLoading}
                onClick={placeOrderHandler}
              >
                {isLoading ? 'Bezig...' : 'Verzend'}
              </Button>
            </ListGroup.Item>
            <ListGroup.Item>
              <Button
                type='button'
                className='btn-block w-100'
                style={{ backgroundColor: "#28a745", borderColor: "#28a745", color: "white" }}
                variant='secondary'
                onClick={() => navigate('/cart')}
              >
                Terug naar Cart
              </Button>
              {isLoading && <Loader />}
            </ListGroup.Item>
          </ListGroup>
        </Card>
      </Col>
    </Row>
  );
};

export default PlaceOrderScreen;